import { useState, type FormEvent } from "react";
import Button from "@/components/base/Button";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dauq3m1ggrtvtk1bgd40";

type Status = "idle" | "loading" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }

    const nombre = String(formData.get("nombre") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const mensaje = String(formData.get("mensaje") ?? "").trim();

    if (!nombre || !email || !mensaje) {
      setStatus("error");
      setFormError("Por favor verifica los campos marcados: nombre, correo y mensaje son obligatorios.");
      return;
    }

    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, typeof value === "string" ? value : value.name);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim().length > 0 && !isSpam
            ? serverMsg
            : "No pudimos enviar tu mensaje. Intenta nuevamente en unos minutos.",
        );
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("No pudimos enviar tu mensaje. Revisa tu conexión e intenta nuevamente.");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";
  const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-700";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="text-xl font-heading font-semibold text-foreground-950 md:text-2xl">
        Envíanos un mensaje
      </h2>
      <p className="mt-2 text-sm text-foreground-600">
        Completa el formulario y nuestro equipo de atención se pondrá en contacto contigo.
      </p>

      <form data-readdy-form id="contacto-form" onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="contacto-nombre" className={labelClass}>
              Nombre completo <span className="text-primary-600">*</span>
            </label>
            <input
              id="contacto-nombre"
              name="nombre"
              type="text"
              autoComplete="name"
              placeholder="Ej. María Fernández"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="contacto-email" className={labelClass}>
              Correo electrónico <span className="text-primary-600">*</span>
            </label>
            <input
              id="contacto-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="tucorreo@ejemplo.com"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="contacto-telefono" className={labelClass}>
              Teléfono
            </label>
            <input
              id="contacto-telefono"
              name="telefono"
              type="tel"
              autoComplete="tel"
              placeholder="+51 987 654 321"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="contacto-asunto" className={labelClass}>
              Asunto
            </label>
            <input
              id="contacto-asunto"
              name="asunto"
              type="text"
              placeholder="Motivo de tu consulta"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="contacto-mensaje" className={labelClass}>
            Mensaje <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="contacto-mensaje"
            name="mensaje"
            rows={5}
            maxLength={500}
            placeholder="Cuéntanos en qué podemos ayudarte (máx. 500 caracteres)."
            className={`${inputClass} resize-none`}
          ></textarea>
        </div>

        <div className="axonia-reserved-field" aria-hidden="true">
          <label htmlFor="contacto-website-alt">No completar este campo</label>
          <input
            id="contacto-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>

        {status === "error" && (
          <p role="alert" className="flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-2.5 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {formError}
          </p>
        )}

        {status === "success" && (
          <p role="status" className="flex items-start gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            ¡Mensaje enviado! Gracias por escribirnos, te responderemos a la brevedad.
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          icon={status === "loading" ? "ri-loader-4-line" : "ri-send-plane-line"}
          disabled={status === "loading"}
          fullWidth
        >
          {status === "loading" ? "Enviando mensaje..." : "Enviar mensaje"}
        </Button>
      </form>
    </div>
  );
}